

import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, Bell } from 'lucide-react'

interface NotificationDropdownProps {
  isOpen: boolean
  onClose: () => void
  isDropdown?: boolean
  dropdownRef?: React.RefObject<HTMLDivElement>
}

export function NotificationDropdown({ isOpen, onClose, isDropdown = false, dropdownRef }: NotificationDropdownProps) {
  // Calculate dropdown position if it's a dropdown
  const getDropdownPosition = () => {
    if (!isDropdown || !dropdownRef?.current) return {}
    const rect = dropdownRef.current.getBoundingClientRect()
    const dropdownWidth = Math.min(320, window.innerWidth - 32)
    const left = Math.max(16, rect.right - dropdownWidth)
    const top = rect.bottom + 8
    
    return {
      position: 'fixed' as const,
      top: `${top}px`,
      left: `${left}px`,
      width: `${dropdownWidth}px`,
      zIndex: 300
    }
  }

  // Notifications should come from API - empty array for now
  const notifications: { id: number; message: string; time: string; read: boolean }[] = []

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Modal/Dropdown */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: isDropdown ? -10 : 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: isDropdown ? -10 : 20 }}
            className={isDropdown ? "z-[300]" : "fixed inset-0 z-50 flex items-center justify-center p-4"}
            style={isDropdown ? getDropdownPosition() : {}}
            onClick={(e) => !isDropdown && e.stopPropagation()}
          >
            <div className={`bg-white/95 dark:bg-slate-900/90 rounded-lg shadow-xl border border-gray-200 dark:border-slate-800 ${isDropdown ? 'max-h-[calc(100vh-5rem)]' : 'max-w-md w-full max-h-[90vh]'} overflow-y-auto backdrop-blur`}>
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-bold text-gray-900">Notifications</h2>
                <button
                  onClick={onClose}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-3">
                {notifications.length === 0 ? (
                  <div className="text-center py-8">
                    <Bell className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <p className="text-gray-600">No notifications</p>
                  </div>
                ) : (
                  notifications.map((notification) => (
                    <div
                      key={notification.id}
                      className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                        notification.read
                          ? 'bg-gray-50 border-gray-200 dark:bg-slate-900/60 dark:border-slate-700'
                          : 'bg-blue-50 border-blue-200 hover:bg-blue-100 dark:bg-blue-500/15 dark:border-blue-400/50 dark:hover:bg-blue-500/25'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                            notification.read
                              ? 'bg-gray-300 dark:bg-slate-600'
                              : 'bg-blue-500 dark:bg-blue-400'
                          }`}
                        ></div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${notification.read ? 'text-gray-700 dark:text-slate-200' : 'text-gray-900 dark:text-slate-50 font-medium'}`}>
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">{notification.time}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}